import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BehindTheScenes = ({ content, theme = 'default' }) => {
  const [activeTab, setActiveTab] = useState('process');

  const getThemeClasses = () => {
    switch (theme) {
      case 'events':
        return {
          active: 'bg-rose-600 text-white',
          inactive: 'text-rose-600 hover:bg-rose-50',
          accent: 'text-rose-600',
          border: 'border-rose-200'
        };
      case 'realestate':
        return {
          active: 'bg-blue-600 text-white',
          inactive: 'text-blue-600 hover:bg-blue-50',
          accent: 'text-blue-600',
          border: 'border-blue-200'
        };
      case 'marketing':
        return {
          active: 'bg-orange-600 text-white',
          inactive: 'text-orange-600 hover:bg-orange-50',
          accent: 'text-orange-600',
          border: 'border-orange-200'
        };
      case 'design':
        return {
          active: 'bg-emerald-600 text-white',
          inactive: 'text-emerald-600 hover:bg-emerald-50',
          accent: 'text-emerald-600',
          border: 'border-emerald-200'
        };
      default:
        return {
          active: 'bg-primary text-white',
          inactive: 'text-primary hover:bg-gray-50',
          accent: 'text-primary',
          border: 'border-gray-200'
        };
    }
  };

  const themeClasses = getThemeClasses();

  const tabs = [
    { id: 'process', label: 'Creative Process', icon: 'Lightbulb' },
    { id: 'equipment', label: 'Equipment Used', icon: 'Camera' },
    { id: 'challenges', label: 'Challenges & Solutions', icon: 'Target' },
    { id: 'team', label: 'Team Insights', icon: 'Users' }
  ];

  const renderTabContent = () => {
    const tabContent = content?.[activeTab];
    
    switch (activeTab) {
      case 'process':
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                  Creative Approach
                </h4>
                <p className="text-gray-600 mb-4">
                  {tabContent?.description}
                </p>
                <ul className="space-y-2">
                  {tabContent?.steps?.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Icon name="ArrowRight" size={14} className={`${themeClasses?.accent} mt-1 flex-shrink-0`} />
                      <span className="text-gray-600 text-sm">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-4">
                {tabContent?.images?.map((img, idx) => (
                  <Image
                    key={idx}
                    src={img?.src}
                    alt={img?.alt}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                ))}
              </div>
            </div>
          </div>
        );

      case 'equipment':
        return (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tabContent?.items?.map((item, idx) => (
              <div key={idx} className={`bg-white rounded-lg border ${themeClasses?.border} p-4`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 ${themeClasses?.active} rounded-lg flex items-center justify-center`}>
                    <Icon name={item?.icon} size={20} color="white" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900">{item?.name}</h5>
                    <p className="text-xs text-gray-500">{item?.category}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3">{item?.description}</p>
                <div className="flex flex-wrap gap-1">
                  {item?.specs?.map((spec, specIdx) => (
                    <span key={specIdx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );

      case 'challenges':
        return (
          <div className="space-y-6">
            {tabContent?.items?.map((challenge, idx) => (
              <div key={idx} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="AlertTriangle" size={20} className="text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h5 className="font-semibold text-gray-900 mb-2">
                      Challenge: {challenge?.problem}
                    </h5>
                    <p className="text-gray-600 mb-4">{challenge?.description}</p>
                    
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 ${themeClasses?.active} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Icon name="CheckCircle" size={20} color="white" />
                      </div>
                      <div>
                        <h6 className="font-medium text-gray-900 mb-2">
                          Solution: {challenge?.solution}
                        </h6>
                        <p className="text-gray-600 text-sm">{challenge?.outcome}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'team':
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {tabContent?.members?.map((member, idx) => (
                <div key={idx} className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-start gap-4">
                    <Image
                      src={member?.avatar}
                      alt={member?.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900">{member?.name}</h5>
                      <p className={`text-sm ${themeClasses?.accent} mb-2`}>{member?.role}</p>
                      <p className="text-gray-600 text-sm mb-3">{member?.insight}</p>
                      
                      <div className="flex flex-wrap gap-2">
                        {member?.skills?.map((skill, skillIdx) => (
                          <span key={skillIdx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-gray-50 rounded-2xl p-8">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Behind the Scenes
        </h3>
        <p className="text-gray-600">
          Discover the creative process, challenges, and expertise behind this project
        </p>
      </div>
      {/* Tab Navigation */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === tab?.id ? themeClasses?.active : themeClasses?.inactive
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span className="hidden sm:inline">{tab?.label}</span>
          </button>
        ))}
      </div>
      {/* Tab Content */}
      <div className="min-h-[400px]">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default BehindTheScenes;